import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Users, Mail, Phone, Calendar, Search, RefreshCw, ShoppingCart, Plus,
  Gavel, Cpu, Star, AlertCircle, DollarSign, Ban, UserCheck, Trash2
} from 'lucide-react';
import {
  COURSE_LABEL, COURSE_VALUE, COURSE_KEYS, BRL,
  getVendaStatusLabel, getVendaStatusBadgeClass, getComboRemainingClass
} from './constants';
import LandingSettingsCard from './LandingSettingsCard';

const COURSE_COLOR = {
  propaganda: { bg: 'bg-orange-100', text: 'text-orange-700', icon: 'text-orange-500', border: 'border-orange-200' },
  digital: { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'text-blue-500', border: 'border-blue-200' },
  combo: { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'text-amber-500', border: 'border-amber-200' },
};
const COURSE_ICON = { propaganda: Gavel, digital: Cpu, combo: Star };

function summarizeVendas(vendas) {
  return vendas.reduce((acc, v) => {
    const c = v.course;
    if (!acc[c]) acc[c] = { total: 0, pago: 0, pendente: 0, cancelado: 0, valor_pago: 0 };
    acc[c].total += 1;
    if (v.payment_status === 'pago') {
      acc[c].pago += 1;
      acc[c].valor_pago += Number(v.value || 0);
    } else if (v.status === 'cancelado') {
      acc[c].cancelado += 1;
    } else {
      acc[c].pendente += 1;
    }
    return acc;
  }, {});
}

export default function VendasTab({
  vendas, vendasLoading, vendasProcessing,
  onCreate, onConfirmPayment, onUpdateStatus, onDelete,
}) {
  const [vendasSearch, setVendasSearch] = useState('');
  const [vendasFilter, setVendasFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVenda, setNewVenda] = useState({ name: '', email: '', phone: '', course: 'combo', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const vendasStats = summarizeVendas(vendas);

  const filteredVendas = vendas.filter((v) => {
    if (vendasFilter !== 'all' && v.course !== vendasFilter) return false;
    if (!vendasSearch) return true;
    const s = vendasSearch.toLowerCase();
    return (v.name || '').toLowerCase().includes(s) ||
           (v.email || '').toLowerCase().includes(s) ||
           (v.phone || '').includes(vendasSearch);
  });

  const totalVendas = vendas.length;
  const totalPago = vendas.filter((v) => v.payment_status === 'pago').length;
  const receitaConfirmada = vendas
    .filter((v) => v.payment_status === 'pago')
    .reduce((s, v) => s + Number(v.value || 0), 0);
  const comboPago = vendasStats.combo?.pago || 0;
  const comboRemaining = Math.max(0, 100 - comboPago);

  const submitNewVenda = async (e) => {
    e.preventDefault();
    if (!newVenda.name || !newVenda.email || !newVenda.phone) return;
    setSubmitting(true);
    const ok = await onCreate({
      ...newVenda,
      value: COURSE_VALUE[newVenda.course] || 0,
    });
    setSubmitting(false);
    if (ok) {
      setNewVenda({ name: '', email: '', phone: '', course: 'combo', notes: '' });
      setShowAddForm(false);
    }
  };

  const filterOptions = [
    { v: 'all', label: 'Todos' },
    { v: 'propaganda', label: 'Propaganda' },
    { v: 'digital', label: 'Direito Digital' },
    { v: 'combo', label: 'Combo' },
  ];

  return (
    <>
      {/* Controle público do contador da landing */}
      <LandingSettingsCard />

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card data-testid="card-total-vendas">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 uppercase font-bold tracking-wide">Total Inscritos</p>
              <Users className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-3xl font-extrabold text-gray-900">{totalVendas}</p>
            <p className="text-xs text-gray-500 mt-1">{totalPago} com pagamento confirmado</p>
          </CardContent>
        </Card>
        <Card data-testid="card-combo-vagas">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 uppercase font-bold tracking-wide">Combo · Vagas 1ª turma</p>
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <p className={`text-3xl font-extrabold ${getComboRemainingClass(comboRemaining)}`}>
              {comboPago}<span className="text-lg text-gray-400">/100</span>
            </p>
            <p className="text-xs text-gray-500 mt-1">{comboRemaining} vagas restantes</p>
          </CardContent>
        </Card>
        <Card data-testid="card-receita">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 uppercase font-bold tracking-wide">Receita confirmada</p>
              <DollarSign className="w-5 h-5 text-emerald-500" />
            </div>
            <p className="text-3xl font-extrabold text-emerald-600">{BRL(receitaConfirmada)}</p>
            <p className="text-xs text-gray-500 mt-1">Apenas pagamentos confirmados</p>
          </CardContent>
        </Card>
        <Card data-testid="card-pendentes">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 uppercase font-bold tracking-wide">Pendentes</p>
              <AlertCircle className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-3xl font-extrabold text-amber-600">{totalVendas - totalPago}</p>
            <p className="text-xs text-gray-500 mt-1">Aguardando confirmação</p>
          </CardContent>
        </Card>
      </div>

      {/* Cards por curso (somatório individualizado) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {COURSE_KEYS.map((c) => {
          const Icon = COURSE_ICON[c];
          const colors = COURSE_COLOR[c];
          const s = vendasStats[c] || { total: 0, pago: 0, pendente: 0, cancelado: 0, valor_pago: 0 };
          return (
            <Card key={c} data-testid={`course-summary-${c}`} className={`${colors.border} border-2`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${colors.icon}`} />
                  </div>
                  <Badge className={`${colors.bg} ${colors.text} border-0`}>{BRL(COURSE_VALUE[c])}</Badge>
                </div>
                <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${colors.text}`}>{COURSE_LABEL[c]}</p>
                <p className="text-3xl font-extrabold text-gray-900">{s.total}</p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-emerald-50 rounded p-2 text-center">
                    <p className="text-emerald-700 font-bold">{s.pago}</p>
                    <p className="text-emerald-600 text-[10px] uppercase">Pago</p>
                  </div>
                  <div className="bg-amber-50 rounded p-2 text-center">
                    <p className="text-amber-700 font-bold">{s.pendente}</p>
                    <p className="text-amber-600 text-[10px] uppercase">Pendente</p>
                  </div>
                  <div className="bg-gray-100 rounded p-2 text-center">
                    <p className="text-gray-700 font-bold">{s.cancelado}</p>
                    <p className="text-gray-500 text-[10px] uppercase">Cancelado</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between text-sm">
                  <span className="text-gray-500">Receita:</span>
                  <span className="font-bold text-emerald-700">{BRL(s.valor_pago)}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Botão adicionar + Formulário */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg">Pré-matrículas Eleições 2026 ({filteredVendas.length})</CardTitle>
          <Button onClick={() => setShowAddForm((v) => !v)} size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-venda-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            {showAddForm ? 'Cancelar' : 'Registrar Pré-matrícula'}
          </Button>
        </CardHeader>

        {showAddForm && (
          <CardContent className="border-t border-gray-100 bg-gray-50">
            <form onSubmit={submitNewVenda} className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4" data-testid="add-venda-form">
              <div>
                <Label className="text-xs uppercase font-semibold text-gray-600">Nome completo *</Label>
                <Input required value={newVenda.name} onChange={(e) => setNewVenda({ ...newVenda, name: e.target.value })} placeholder="João da Silva" data-testid="venda-name" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-gray-600">E-mail *</Label>
                <Input required type="email" value={newVenda.email} onChange={(e) => setNewVenda({ ...newVenda, email: e.target.value })} placeholder="email@dominio.com" data-testid="venda-email" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-gray-600">Telefone *</Label>
                <Input required value={newVenda.phone} onChange={(e) => setNewVenda({ ...newVenda, phone: e.target.value })} placeholder="(31) 99999-9999" data-testid="venda-phone" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-gray-600">Curso adquirido *</Label>
                <select required value={newVenda.course} onChange={(e) => setNewVenda({ ...newVenda, course: e.target.value })}
                  className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="venda-course">
                  <option value="propaganda">Propaganda Clássica · R$ 999,00</option>
                  <option value="digital">Direito Digital e IA · R$ 2.000,00</option>
                  <option value="combo">Combo Eleições 2026 · R$ 2.699,00</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase font-semibold text-gray-600">Observações</Label>
                <Input value={newVenda.notes} onChange={(e) => setNewVenda({ ...newVenda, notes: e.target.value })} placeholder="Opcional" data-testid="venda-notes" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)} disabled={submitting}>Cancelar</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white" disabled={submitting} data-testid="venda-submit-btn">
                  {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Registrar Pré-matrícula'}
                </Button>
              </div>
            </form>
          </CardContent>
        )}
      </Card>

      {/* Filtros + lista */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex gap-2 flex-wrap">
              {filterOptions.map((opt) => (
                <button key={opt.v} onClick={() => setVendasFilter(opt.v)}
                  data-testid={`filter-${opt.v}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${vendasFilter === opt.v ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input placeholder="Buscar por nome, email, telefone..." value={vendasSearch} onChange={(e) => setVendasSearch(e.target.value)} className="pl-10 w-full md:w-80" data-testid="venda-search" />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {vendasLoading ? (
            <div className="text-center py-10"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-amber-500" /><p className="mt-2 text-gray-600">Carregando...</p></div>
          ) : filteredVendas.length === 0 ? (
            <div className="text-center py-10">
              <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">{vendasSearch || vendasFilter !== 'all' ? 'Nenhum resultado para os filtros.' : 'Nenhuma pré-matrícula registrada ainda.'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredVendas.map((v) => (
                <VendaRow
                  key={v.id}
                  venda={v}
                  processing={vendasProcessing.has(v.id)}
                  onConfirmPayment={onConfirmPayment}
                  onUpdateStatus={onUpdateStatus}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function VendaRow({ venda, processing, onConfirmPayment, onUpdateStatus, onDelete }) {
  const colors = COURSE_COLOR[venda.course] || COURSE_COLOR.combo;
  const Icon = COURSE_ICON[venda.course] || ShoppingCart;
  const isPago = venda.payment_status === 'pago';
  const isCanc = venda.status === 'cancelado';
  const rowClass = isCanc ? 'bg-gray-50 opacity-70' : 'bg-white hover:bg-gray-50';

  return (
    <div data-testid={`venda-row-${venda.id}`} className={`border rounded-lg p-4 transition-colors ${rowClass}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className={`w-12 h-12 rounded-lg ${colors.bg} flex items-center justify-center shrink-0`}>
          <Icon className={`w-6 h-6 ${colors.icon}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="text-base font-bold text-gray-900">{venda.name}</h3>
            <Badge className={`${colors.bg} ${colors.text} border-0 text-[10px]`}>{COURSE_LABEL[venda.course]}</Badge>
            <Badge className={getVendaStatusBadgeClass(venda)}>{getVendaStatusLabel(venda)}</Badge>
            <span className="text-xs font-bold text-emerald-700">{BRL(venda.value)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-600">
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{venda.email}</span>
            <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{venda.phone}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(venda.data_compra).toLocaleString('pt-BR')}</span>
          </div>
          {venda.notes && <p className="text-xs text-gray-500 mt-1.5 italic">"{venda.notes}"</p>}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {!isPago && !isCanc && (
            <Button size="sm" onClick={() => onConfirmPayment(venda.id, venda.name)} disabled={processing}
              className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`confirm-payment-${venda.id}`}>
              {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4 mr-1" />Confirmar Pagamento</>}
            </Button>
          )}
          {!isCanc && (
            <Button size="sm" variant="outline" onClick={() => onUpdateStatus(venda.id, venda.name, 'cancelado')} disabled={processing}
              className="border-gray-300 text-gray-700 hover:bg-gray-50" data-testid={`cancel-${venda.id}`}>
              <Ban className="w-4 h-4 mr-1" />Cancelar
            </Button>
          )}
          {isCanc && (
            <Button size="sm" variant="outline" onClick={() => onUpdateStatus(venda.id, venda.name, 'pendente')} disabled={processing}
              className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`reactivate-${venda.id}`}>
              <UserCheck className="w-4 h-4 mr-1" />Reativar
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onDelete(venda.id, venda.name)} disabled={processing}
            className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-${venda.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
